import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FLOWS_ENUM } from 'src/common/enums';
import {
  selectCurrentFlow,
  selectFlowSteps,
} from 'src/store/slices/flow/flowSelectors';
import type { Step } from 'src/types/flow';
import { selectConfigExpressSettingsName } from 'src/store/slices/config/configSelectors';

const PORTAL_SUFFIX = 'XCP Portal';

const FLOW_NAMES: Record<string, string> = {
  [FLOWS_ENUM.APPLY_LICENSE]: 'Apply',
  [FLOWS_ENUM.UPDATE_CERTIFICATE]: 'Update Certificate',
  [FLOWS_ENUM.RENEW_LICENSE]: 'Renew',
  [FLOWS_ENUM.BULK_RENEWAL]: 'Bulk Renewal',
  [FLOWS_ENUM.PAY_TOT]: 'Tax Filing',
  [FLOWS_ENUM.ADMIN_UPDATE]: 'Admin Update',
};

const getStaticPageTitles = (
  placeDisplayName: string
): Record<string, string> => ({
  '': `${placeDisplayName} | ${PORTAL_SUFFIX}`,
  dashboard: `Dashboard | ${PORTAL_SUFFIX}`,
  'email-check': `Check Email | ${PORTAL_SUFFIX}`,
  'create-account': `Create Account | ${PORTAL_SUFFIX}`,
  'account-update': `Account Update | ${PORTAL_SUFFIX}`,
  'all-permits': `All Permits | ${PORTAL_SUFFIX}`,
  'single-permit': `Permit Details | ${PORTAL_SUFFIX}`,
  'apply-success': `Application Submitted | ${PORTAL_SUFFIX}`,
  'renew-success': `Renewal Complete | ${PORTAL_SUFFIX}`,
  'update-success': `Update Complete | ${PORTAL_SUFFIX}`,
  'tax-success': `Tax Filing Complete | ${PORTAL_SUFFIX}`,
  'admin-update-success': `Update Complete | ${PORTAL_SUFFIX}`,
  'payment-redirect': `Payment | ${PORTAL_SUFFIX}`,
  'payment-completed': `Payment Completed | ${PORTAL_SUFFIX}`,
  'payment-success': `Payment Successful | ${PORTAL_SUFFIX}`,
  'payment-pending': `Payment Pending | ${PORTAL_SUFFIX}`,
  'payment-error': `Payment Error | ${PORTAL_SUFFIX}`,
  'payment-cybersource': `Payment | ${PORTAL_SUFFIX}`,
  'payment-cardconnect': `Payment | ${PORTAL_SUFFIX}`,
  error: `Error | ${PORTAL_SUFFIX}`,
});

const useDocumentTitle = () => {
  const location = useLocation();
  const placeDisplayName = useSelector(
    selectConfigExpressSettingsName
  ) as string;
  const currentFlow = useSelector(selectCurrentFlow) as string | undefined;
  const steps = useSelector(selectFlowSteps) as Step[] | undefined;

  useEffect(() => {
    // segments[0] = city identifier (e.g. 'boston-str-portal')
    // segments[1] = main page segment (e.g. 'apply-property')
    // segments[2] = optional dynamic param (e.g. application number)
    const segments = location.pathname.split('/').filter(Boolean);
    const pageSegment = segments[1] ?? '';

    const matchedStep = steps?.find((step) => step.key === pageSegment);
    if (matchedStep && currentFlow) {
      const flowName = FLOW_NAMES[currentFlow] ?? currentFlow;
      document.title = `${matchedStep.stepName} — ${flowName} | ${PORTAL_SUFFIX}`;
      return;
    }

    const staticTitle = getStaticPageTitles(placeDisplayName)[pageSegment];
    if (staticTitle) {
      document.title = staticTitle;
      return;
    }

    document.title = PORTAL_SUFFIX;
  }, [location.pathname, currentFlow, steps, placeDisplayName]);
};

export default useDocumentTitle;
